<!-- Cyber Sidebar -->
<aside
  class="cyber-sidebar"
  [class.mobile-open]="sideBarvisible"
  [class.collapsed]="!isMobile && collapsed"
  (click)="$event.stopPropagation()"
  role="navigation"
  aria-label="Main navigation"
  (keydown.enter)="toggleCollapse()"
  (keydown.space)="toggleCollapse()"
  tabindex="0"
  >
  <!-- Cyber Controls Panel -->
  @if (!isMobile) {
    <div class="cyber-controls">
      <button
        type="button"
        class="cyber-collapse-toggle"
        (click)="toggleCollapse()"
        [attr.aria-pressed]="collapsed"
        [aria-label]="collapsed ? 'Expand sidebar' : 'Collapse sidebar'"
        [title]="collapsed ? 'Expand' : 'Collapse'"
        >
        <i
          class="fa"
          [ngClass]="collapsed ? 'fa-angles-right' : 'fa-angles-left'"
          aria-hidden="true"
        ></i>
        <span class="visually-hidden">{{
          collapsed ? "Expand sidebar" : "Collapse sidebar"
        }}</span>
      </button>
    </div>
  }

  <!-- Cyber Company Profile -->
  @if (companyModel) {
    <div class="cyber-company-profile">
      <div class="company-avatar">
        <a [routerLink]="'/dashboard/company'" [title]="companyModel.name">
          <img
            title="Company Logo"
            [src]="companyModel.image_url || defaultImageUrl"
            [alt]="
              companyModel.image_url
                ? companyModel.name + ' logo'
                : 'Default placeholder logo for ' + companyModel.name
            "
            [title]="companyModel.name + ' logo'"
            role="img"
            />
        </a>
      </div>
      @if (companyModel.name) {
        <div class="company-name" [innerText]="companyModel.name"></div>
      }
    </div>

    <!-- Tier Info Panel -->
    <div class="cyber-tier-panel" [class]="tierBadgeClass">
      <div class="tier-header">
        <span class="tier-badge" [class]="tierBadgeClass">
          <i class="fa fa-star" aria-hidden="true"></i>
          <span>{{ tierDisplayName }}</span>
        </span>
        @if (trialDaysRemaining !== null && trialDaysRemaining >= 0) {
          <span class="trial-countdown" [class.urgent]="trialDaysRemaining <= 3">
            <i class="fa fa-clock" aria-hidden="true"></i>
            {{ trialDaysRemaining }} days left
          </span>
        }
      </div>

      @if (isPartnerCompany) {
        <!-- Channel-partner company (#943): no upgrade prompts. -->
        <div class="tier-details">
          <div class="ai-queries-info">
            <span class="queries-label">
              <i class="fa fa-info-circle" aria-hidden="true"></i>
              {{ partnerPlanMessage }}
            </span>
          </div>
        </div>
      }

      @if (currentTier === 'free' && !isPartnerCompany) {
        <div class="tier-details">
          <div class="ai-queries-info">
            <span class="queries-label">
              <i class="fa fa-info-circle" aria-hidden="true"></i>
              Choose a plan to start scanning
            </span>
          </div>
          <a
            [routerLink]="['/dashboard/company/subscription-plans']"
            class="upgrade-link"
            >
            <i class="fa fa-arrow-right" aria-hidden="true"></i>
            Choose a Plan
          </a>
        </div>
      }

      @if (currentTier === 'trial' && !isPartnerCompany) {
        <div class="tier-details">
          <div class="trial-benefits">
            <i class="fa fa-check-circle" aria-hidden="true"></i>
            <span>Full access during your trial</span>
          </div>
          <a
            [routerLink]="['/dashboard/company']"
            [queryParams]="{ upgrade: 'true' }"
            class="upgrade-link"
            >
            <i class="fa fa-crown" aria-hidden="true"></i>
            Choose a plan
          </a>
        </div>
      }

      @if (currentTier === 'expired' && !isPartnerCompany) {
        <div class="tier-details">
          <div class="expired-message">
            <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
            <span>Plan ended</span>
          </div>
          <a
            [routerLink]="['/dashboard/company']"
            [queryParams]="{ upgrade: 'true' }"
            class="upgrade-link urgent"
            >
            <i class="fa fa-unlock" aria-hidden="true"></i>
            Restore access
          </a>
        </div>
      }

      @if (currentTier === 'premium') {
        <div class="tier-details premium">
          <div class="premium-badge">
            <i class="fa fa-infinity" aria-hidden="true"></i>
            <span>Unlimited Access</span>
          </div>
        </div>
      }
    </div>
  }

  <!-- Get Started Wizard -->
  <app-get-started-wizard></app-get-started-wizard>

  <!-- Cyber Navigation -->
  <nav class="cyber-nav" aria-label="Main navigation menu">
    <!-- Dashboard -->
    <div class="nav-item">
      <a
        class="cyber-nav-link"
        [routerLink]="'/dashboard'"
        routerLinkActive="active"
        [routerLinkActiveOptions]="{ exact: true }"
        aria-label="Dashboard"
        (click)="onNavigationClick()"
        >
        <div class="nav-icon">
          <i class="fa fa-gauge" aria-hidden="true"></i>
        </div>
        <span class="nav-title">Dashboard</span>
      </a>
    </div>

    <!-- Dynamic Navigation Items -->
    @for (navitem of sidebarLinks; track navitem.title; let i = $index) {
      <div class="nav-item" [class.future-item]="navitem.future" [class.locked-item]="isNavItemLocked(navitem)">
        @if (navitem.future) {
          <!-- Future Items -->
          <div class="cyber-nav-link disabled" aria-disabled="true">
            <div class="nav-icon">
              <i class="fa {{ navitem.iconImg }}" aria-hidden="true"></i>
            </div>
            <span class="nav-title">{{ navitem.title }}</span>
          </div>
          <div class="coming-soon">
            <small>Coming Soon</small>
          </div>
        } @else if (isNavItemLocked(navitem) && !navitem.sub_links) {
          <!-- Locked Simple Navigation Links -->
          <button
            type="button"
            class="cyber-nav-link locked"
            (click)="onLockedFeatureClick(navitem)"
            [aria-label]="navitem.title + ' (Upgrade Required)'"
            [title]="navitem.requiredAddon ? 'Requires ' + navitem.requiredAddon + ' addon' : 'Upgrade to access'"
            >
            <div class="nav-icon">
              <i class="fa {{ navitem.iconImg }}" aria-hidden="true"></i>
            </div>
            <span class="nav-title">{{ navitem.title }}</span>
            <span class="lock-icon">
              <i class="fa fa-lock" aria-hidden="true"></i>
            </span>
          </button>
        } @else if (!navitem.sub_links) {
          <!-- Simple Navigation Links -->
          <!-- <a class="cyber-nav-link" [routerLink]="navitem.route" routerLinkActive="active" [routerLinkActiveOptions]="{ exact: true }" [aria-label]="navitem.title"> -->
          <a
            class="cyber-nav-link"
            [routerLink]="navitem.route"
            routerLinkActive="active"
            [routerLinkActiveOptions]="{ exact: true }"
            [aria-label]="navitem.title"
            (click)="onNavigationClick()"
            (keydown.enter)="onNavigationClick()"
            (keydown.space)="onNavigationClick()"
            tabindex="0"
            >
            <div class="nav-icon">
              <i class="fa {{ navitem.iconImg }}" aria-hidden="true"></i>
            </div>
            <span class="nav-title">{{ navitem.title }}</span>
            @if (isObservable(navitem.count)) {
              @if (navitem.count | async; as count) {
                @if (count !== null) {
                  <span
                    class="cyber-badge"
                    [class.is-zero]="!count || count === '0'"
                    [textContent]="count"
                    [aria-label]="count + ' items'"
                  ></span>
                }
              }
            }
          </a>
        } @else {
          <!-- Navigation with Children -->
          <button
            type="button"
            class="cyber-nav-toggle"
            (click)="toggleActiveMenu(navitem.title)"
            [attr.aria-expanded]="activeMenu === navitem.title"
            [attr.aria-controls]="'submenu-' + i"
            [aria-label]="navitem.title"
            [title]="collapsed ? navitem.title : null"
            >
            <div class="nav-icon">
              <i class="fa {{ navitem.iconImg }}" aria-hidden="true"></i>
            </div>
            <span class="nav-title">{{ navitem.title }}</span>
            @if (isObservable(navitem.count)) {
              @if (navitem.count | async; as count) {
                @if (count !== null) {
                  <span
                    class="cyber-badge"
                    [class.is-zero]="!count || count === '0'"
                    [textContent]="count"
                    [aria-label]="count + ' items'"
                  ></span>
                }
              }
            }
            <span
              class="nav-caret"
              [class.expanded]="activeMenu === navitem.title"
              aria-hidden="true"
              >
              <i
                class="fa"
                [ngClass]="
                  activeMenu === navitem.title
                    ? 'fa-caret-down'
                    : 'fa-caret-right'
                "
              ></i>
            </span>
          </button>

          <!-- Sub-navigation -->
          @if (
            !collapsed && navitem.sub_links && activeMenu === navitem.title
            ) {
            <nav
              class="cyber-subnav"
              [id]="'submenu-' + i"
              [aria-label]="navitem.title + ' submenu'"
              >
              @for (sublink of navitem.sub_links; track sublink.title) {
                <div class="nav-item" [class.future-item]="sublink.future" [class.locked-item]="isNavItemLocked(sublink)" [class.has-children]="sublink.sub_links">
                  @if (sublink.future) {
                    <!-- Future Sub-items -->
                    <div class="cyber-nav-link disabled" aria-disabled="true">
                      <div class="nav-icon">
                        <i
                          class="fa {{ sublink.iconImg }}"
                          aria-hidden="true"
                        ></i>
                      </div>
                      <span class="nav-title">{{ sublink.title }}</span>
                    </div>
                    <div class="coming-soon">
                      <small>Coming Soon</small>
                    </div>
                  } @else if (sublink.sub_links) {
                    <!-- Second-level group with third-level children -->
                    <button
                      type="button"
                      class="cyber-nav-toggle cyber-subnav-toggle"
                      (click)="toggleActiveSubMenu(navitem.title, sublink.title)"
                      [attr.aria-expanded]="activeSubMenu === buildSubMenuKey(navitem.title, sublink.title)"
                      [aria-label]="sublink.title"
                      >
                      <div class="nav-icon">
                        <i
                          class="fa {{ sublink.iconImg }}"
                          aria-hidden="true"
                        ></i>
                      </div>
                      <span class="nav-title">{{ sublink.title }}</span>
                      @if (isObservable(sublink.count)) {
                        @if (sublink.count | async; as count) {
                          @if (count !== null) {
                            <span
                              class="cyber-badge"
                              [class.is-zero]="!count || count === '0'"
                              [textContent]="count"
                              [aria-label]="count + ' items'"
                            ></span>
                          }
                        }
                      }
                      <span
                        class="nav-caret"
                        [class.expanded]="activeSubMenu === buildSubMenuKey(navitem.title, sublink.title)"
                        aria-hidden="true"
                        >
                        <i
                          class="fa"
                          [ngClass]="
                            activeSubMenu === buildSubMenuKey(navitem.title, sublink.title)
                              ? 'fa-caret-down'
                              : 'fa-caret-right'
                          "
                        ></i>
                      </span>
                    </button>

                    @if (activeSubMenu === buildSubMenuKey(navitem.title, sublink.title)) {
                      <nav
                        class="cyber-subnav cyber-subnav-level-2"
                        [aria-label]="sublink.title + ' submenu'"
                        >
                        @for (childlink of sublink.sub_links; track childlink.title) {
                          <div class="nav-item" [class.future-item]="childlink.future" [class.locked-item]="isNavItemLocked(childlink)">
                            @if (childlink.future) {
                              <div class="cyber-nav-link disabled" aria-disabled="true">
                                <div class="nav-icon">
                                  <i class="fa {{ childlink.iconImg }}" aria-hidden="true"></i>
                                </div>
                                <span class="nav-title">{{ childlink.title }}</span>
                              </div>
                              <div class="coming-soon">
                                <small>Coming Soon</small>
                              </div>
                            } @else if (isNavItemLocked(childlink)) {
                              <button
                                type="button"
                                class="cyber-nav-link locked"
                                (click)="onLockedFeatureClick(childlink)"
                                [aria-label]="childlink.title + ' (Upgrade Required)'"
                                [title]="childlink.requiredAddon ? 'Requires ' + childlink.requiredAddon + ' addon' : 'Upgrade to access'"
                                >
                                <div class="nav-icon">
                                  <i class="fa {{ childlink.iconImg }}" aria-hidden="true"></i>
                                </div>
                                <span class="nav-title">{{ childlink.title }}</span>
                                <span class="lock-icon">
                                  <i class="fa fa-lock" aria-hidden="true"></i>
                                </span>
                              </button>
                            } @else {
                              <a
                                [routerLink]="childlink.route"
                                routerLinkActive="active"
                                [aria-label]="childlink.title"
                                class="cyber-nav-link cyber-nav-link-level-3"
                                (click)="onSubmenuClick()"
                                >
                                <div class="nav-icon">
                                  <i class="fa {{ childlink.iconImg }}" aria-hidden="true"></i>
                                </div>
                                <span class="nav-title">{{ childlink.title }}</span>
                                @if (isObservable(childlink.count)) {
                                  @if (childlink.count | async; as count) {
                                    @if (count !== null) {
                                      <span
                                        class="cyber-badge"
                                        [class.is-zero]="!count || count === '0'"
                                        [textContent]="count"
                                        [aria-label]="count + ' items'"
                                      ></span>
                                    }
                                  }
                                }
                              </a>
                            }
                          </div>
                        }
                      </nav>
                    }
                  } @else if (isNavItemLocked(sublink)) {
                    <!-- Locked Sub-links -->
                    <button
                      type="button"
                      class="cyber-nav-link locked"
                      (click)="onLockedFeatureClick(sublink)"
                      [aria-label]="sublink.title + ' (Upgrade Required)'"
                      [title]="sublink.requiredAddon ? 'Requires ' + sublink.requiredAddon + ' addon' : 'Upgrade to access'"
                      >
                      <div class="nav-icon">
                        <i
                          class="fa {{ sublink.iconImg }}"
                          aria-hidden="true"
                        ></i>
                      </div>
                      <span class="nav-title">{{ sublink.title }}</span>
                      <span class="lock-icon">
                        <i class="fa fa-lock" aria-hidden="true"></i>
                      </span>
                    </button>
                  } @else {
                    <!-- Active Sub-links -->
                    <a
                      [routerLink]="sublink.route"
                      routerLinkActive="active"
                      [aria-label]="sublink.title"
                      class="cyber-nav-link"
                      (click)="onSubmenuClick()"
                      >
                      <div class="nav-icon">
                        <i
                          class="fa {{ sublink.iconImg }}"
                          aria-hidden="true"
                        ></i>
                      </div>
                      <span class="nav-title">{{ sublink.title }}</span>
                      @if (isObservable(sublink.count)) {
                        @if (sublink.count | async; as count) {
                          @if (count !== null) {
                            <span
                              class="cyber-badge"
                              [class.is-zero]="!count || count === '0'"
                              [textContent]="count"
                              [aria-label]="count + ' items'"
                            ></span>
                          }
                        }
                      }
                    </a>
                  }
                </div>
              }
            </nav>
          }
        }
      </div>
    }
  </nav>

  <!-- Ninja Coins Widget -->
  @if (creditSummary) {
    <a
      class="cyber-credits-widget"
      [routerLink]="['/dashboard/company']"
      [fragment]="'sni-tokens'"
      [title]="'Coins: ' + ninjaCreditsService.formatCredits(creditSummary.totalAvailable) + ' remaining'"
      aria-label="View Coins balance"
      >
      <div class="credits-icon">
        <i class="fas fa-coins text-warning" aria-hidden="true"></i>
      </div>
      @if (!collapsed) {
        <div class="credits-details">
          <div class="credits-label">Coins</div>
          <div class="credits-balance">
            {{ ninjaCreditsService.formatCredits(creditSummary.totalAvailable) }}
            <span class="credits-allotment">/ {{ ninjaCreditsService.formatCredits(creditSummary.monthlyAllotment) }}</span>
          </div>
          <div class="credits-bar">
            <div
              class="credits-bar-fill bg-{{ getCreditColorClass() }}"
              [style.width.%]="getCreditBurnPercent()"
              role="progressbar"
              aria-label="SNI coin usage"
              [attr.aria-valuenow]="getCreditBurnPercent()"
              aria-valuemin="0"
              aria-valuemax="100"
            ></div>
          </div>
        </div>
      }
    </a>
  }
</aside>

<!-- Mobile Backdrop -->
@if (isMobile && sideBarvisible) {
  <div
    class="sidebar-backdrop visible"
    (click)="onSidebarBackdropClick()"
    (touchstart)="onSidebarBackdropClick()"
    (pointerdown)="onSidebarBackdropClick()"
    role="button"
    tabindex="0"
    aria-label="Close sidebar"
  ></div>
}
